import {
  LanguageDriftGuard,
  parseResponseDone,
  ResponseCoordinator,
  TOOL_NAMES,
  type CoordinatorClock,
  type GiveUpReason,
  type LanguageDrift,
  type ResponseRequest,
} from '@acs/shared';
import type { TestSessionStart, TurnEvent } from '../../shared/api/testCall.js';

/**
 * The browser side of a test call (doc 33 W10): the SAME ResponseCoordinator
 * the phone gateway runs, driven by the WebRTC data channel.
 *
 * What is shared with the phone: response ownership (VAD detects turns, the
 * coordinator creates every response), one continuation per tool batch,
 * intent-specific retries, the reprompt and closing-rescue texts and timings
 * (sent by the server), and the booking evidence (`assistant_speech` only when
 * a response finished PLAYING, `caller_turn` when the caller's turn was
 * committed).
 *
 * What differs, by transport (doc 33 W10 #1): WebRTC playback is managed by
 * the provider, so "heard" is `output_audio_buffer.stopped` and an interruption
 * is `output_audio_buffer.clear`, which the provider truncates itself — the
 * carrier mark and truncate logic is NOT copied here.
 */
export interface BrowserCallDeps {
  send: (event: Record<string, unknown>) => void;
  execTool: (name: string, callId: string, args: unknown, events: TurnEvent[]) => Promise<unknown>;
  clock: CoordinatorClock;
  policy: TestSessionStart['policy'];
  greetingToolChoice: 'none' | 'auto';
  onTranscript: (role: 'assistant' | 'you', text: string) => void;
  onToolTurn: (name: string, args: unknown, result: unknown) => void;
  /** The call should end now (goodbye heard, silence, or the model gave up). */
  onEnd: (reason: 'goodbye' | 'silence' | 'model_unavailable') => void;
  log?: (msg: string, detail?: unknown) => void;
}

/** Most silence prompts before the call is closed (same as the phone). */
const MAX_SILENCE_STRIKES = 2;
/** How long a tool call waits for the caller's words still being transcribed
 * (the phone gateway waits the same way, server-side). */
const TRANSCRIPT_WAIT_MS = 1_500;

interface OaiEvent {
  type: string;
  transcript?: string;
  name?: string;
  call_id?: string;
  response_id?: string;
  item_id?: string;
  arguments?: string;
  response?: Parameters<typeof parseResponseDone>[0]['response'];
  error?: { message?: string; code?: string; type?: string; event_id?: string };
}

export class BrowserCall {
  readonly coordinator: ResponseCoordinator;
  private events: TurnEvent[] = [];
  private playing = false;
  private userSpeaking = false;
  private ending = false;
  private spokeSinceCaller = false;
  private rescued = false;
  private strikes = 0;
  private idle: (() => void) | null = null;
  private farewell: (() => void) | null = null;
  private pendingTools = 0;
  private closed = false;
  /** Tool calls run ONE AT A TIME (2026-10-08): two cancellations sent in
   * parallel each loaded the session, only the first carried the caller's
   * "Tak", and the second was refused and asked again. */
  private toolQueue: Promise<void> = Promise.resolve();
  /** Caller turns whose transcript has not arrived yet. */
  private readonly awaitingText = new Set<string>();
  private textWaiters: (() => void)[] = [];
  /** The model's language against the caller's words, as on the phone (2026-10-10). */
  private readonly language = new LanguageDriftGuard();

  constructor(private readonly d: BrowserCallDeps) {
    this.coordinator = new ResponseCoordinator(
      {
        createResponse: (r: ResponseRequest) =>
          d.send({
            type: 'response.create',
            event_id: r.eventId,
            ...(r.instructions !== undefined || r.toolChoice !== undefined
              ? { response: { ...(r.instructions !== undefined ? { instructions: r.instructions } : {}), ...(r.toolChoice !== undefined ? { tool_choice: r.toolChoice } : {}) } }
              : {}),
          }),
        cancelResponse: (id) => d.send({ type: 'response.cancel', ...(id ? { response_id: id } : {}) }),
      },
      d.clock,
      {
        maxRetries: d.policy.maxRetries,
        maxCallerWaitMs: d.policy.maxCallerWaitMs,
        rateLimitWaitMs: d.policy.rateLimitWaitMs ?? d.policy.maxCallerWaitMs,
        stallMs: d.policy.stallMs,
        firstOutputMs: d.policy.firstOutputMs ?? 0,
      },
      { warn: (o, m) => d.log?.(m ?? 'warn', o), error: (o, m) => d.log?.(m ?? 'error', o) },
      {
        onGiveUp: (intent, reason: GiveUpReason) => {
          d.log?.('model gave up', { intent, reason });
          this.end('model_unavailable');
        },
        onFinished: (f) => {
          if (f.retrying) return;
          // end_call in a response that said nothing: ask for the goodbye once.
          if (this.ending && !this.spokeSinceCaller && !this.rescued) {
            this.rescued = true;
            this.coordinator.request('closing_rescue', { instructions: d.policy.closingNudge, toolChoice: 'none' });
          }
          this.updateIdle();
        },
        onActivityChanged: () => this.updateIdle(),
      },
    );
  }

  /** The data channel opened: greet, exactly like the phone. */
  begin(): void {
    this.coordinator.request('greeting', { toolChoice: this.d.greetingToolChoice });
  }

  async handle(raw: string): Promise<void> {
    if (this.closed) return;
    let ev: OaiEvent;
    try {
      ev = JSON.parse(raw) as OaiEvent;
    } catch {
      return;
    }
    switch (ev.type) {
      case 'response.created':
        if (ev.response && (ev.response as { id?: string }).id) this.coordinator.onResponseCreated((ev.response as { id: string }).id);
        break;
      case 'response.done': {
        const done = parseResponseDone(ev as Parameters<typeof parseResponseDone>[0]);
        if (done) this.coordinator.onResponseDone(done);
        break;
      }
      case 'error':
        // Correlated by event id; never fatal by itself (same as the phone).
        this.coordinator.onRequestError({
          eventId: ev.error?.event_id ?? null,
          code: ev.error?.code,
          type: ev.error?.type,
          message: ev.error?.message ?? 'realtime error',
        });
        break;
      case 'input_audio_buffer.speech_started':
        this.userSpeaking = true;
        this.strikes = 0;
        this.spokeSinceCaller = false;
        this.coordinator.onCallerSpeechStarted();
        if (this.playing || this.coordinator.activeResponseId) {
          // Barge-in. WebRTC: the provider truncates what was not played.
          this.coordinator.cancelActive();
          this.d.send({ type: 'output_audio_buffer.clear' });
        }
        this.updateIdle();
        break;
      case 'input_audio_buffer.speech_stopped':
        this.userSpeaking = false;
        this.coordinator.onCallerSpeechStopped();
        break;
      case 'input_audio_buffer.committed':
        this.userSpeaking = false;
        if (this.ending) break;
        this.events.push({ kind: 'caller_turn', itemId: ev.item_id ?? null });
        if (ev.item_id) this.awaitingText.add(ev.item_id);
        this.language.callerTurn();
        this.coordinator.onCallerTurnCommitted();
        break;
      case 'response.output_audio_transcript.delta':
        // Proof of life for the first-output watchdog (as on the phone).
        this.coordinator.onResponseOutput(ev.response_id ?? null);
        break;
      case 'output_audio_buffer.started':
        this.coordinator.onResponseOutput(ev.response_id ?? this.coordinator.activeResponseId);
        this.playing = true;
        this.spokeSinceCaller = true;
        this.updateIdle();
        break;
      case 'output_audio_buffer.stopped':
        // Played to the end: this is what counts as "the caller heard it".
        this.playing = false;
        this.events.push('assistant_speech');
        if (this.ending) this.end('goodbye');
        this.updateIdle();
        break;
      case 'output_audio_buffer.cleared':
        // Interrupted: NOT heard in full — no assistant_speech.
        this.playing = false;
        this.updateIdle();
        break;
      case 'response.output_audio_transcript.done':
      case 'response.audio_transcript.done':
        if (ev.transcript) {
          this.d.onTranscript('assistant', ev.transcript);
          this.coordinator.onResponseText(ev.response_id ?? null, ev.transcript);
          this.correctLanguage(this.language.reply(ev.transcript));
        }
        break;
      case 'conversation.item.input_audio_transcription.failed':
        this.textArrived(ev.item_id);
        this.correctLanguage(this.language.caller(''));
        break;
      case 'conversation.item.input_audio_transcription.completed':
        this.textArrived(ev.item_id);
        if (ev.transcript) {
          this.d.onTranscript('you', ev.transcript);
          // The words of that turn, for the server's "was it a yes?" check.
          this.events.push({ kind: 'caller_text', itemId: ev.item_id ?? null, text: ev.transcript });
        }
        this.correctLanguage(this.language.caller(ev.transcript ?? ''));
        break;
      case 'response.function_call_arguments.done':
        await this.tool(ev);
        break;
      default:
        break;
    }
  }

  private async tool(ev: OaiEvent): Promise<void> {
    if (!ev.call_id || !ev.name) return;
    this.coordinator.onToolCall(ev.call_id, ev.response_id ?? null);
    let args: unknown = {};
    try {
      args = ev.arguments ? JSON.parse(ev.arguments) : {};
    } catch {
      args = {};
    }
    this.pendingTools += 1;
    const call = { ...ev, call_id: ev.call_id, name: ev.name };
    const run = this.toolQueue.then(() => this.runTool(call, args));
    this.toolQueue = run.then(
      () => undefined,
      () => undefined,
    );
    await run;
  }

  private async runTool(ev: OaiEvent & { call_id: string; name: string }, args: unknown): Promise<void> {
    // The caller's "Tak" is the evidence the server checks: send its words,
    // not just the fact that they spoke (the model often answers first).
    await this.transcriptsSettled();
    if (this.closed) {
      // Hung up while this call waited its turn: never run it after the end.
      this.pendingTools -= 1;
      return;
    }
    const events = this.events.splice(0);
    let result: unknown;
    try {
      result = await this.d.execTool(ev.name, ev.call_id, args, events);
    } catch (e) {
      // A transport failure is an error, never an answer about the diary.
      result = { ok: false, reason: 'internal_error', error: (e as Error).message, retryable: true };
    } finally {
      this.pendingTools -= 1;
    }
    if (this.closed) return;
    this.d.onToolTurn(ev.name, args, result);
    this.d.send({ type: 'conversation.item.create', item: { type: 'function_call_output', call_id: ev.call_id, output: JSON.stringify(result) } });
    const isEnd = ev.name === 'end_call';
    if (isEnd) {
      this.ending = true;
      this.farewell ??= this.d.clock.setTimer(this.d.policy.farewellMs, () => this.end('goodbye'));
    }
    // Silent bookkeeping after an answer needs no second response (2026-10-08).
    this.coordinator.settleTool(ev.call_id, isEnd ? false : ev.name === TOOL_NAMES.noteUnmetRequest ? 'unless_answered' : true);
  }

  /** The model answered in a language the caller is not speaking: tell it, for its next reply. */
  private correctLanguage(drift: LanguageDrift | null): void {
    if (!drift || this.closed) return;
    this.d.log?.('language drift corrected', { expected: drift.expected, said: drift.said });
    this.d.send({ type: 'conversation.item.create', item: { type: 'message', role: 'system', content: [{ type: 'input_text', text: drift.instruction }] } });
  }

  private textArrived(itemId: string | undefined): void {
    if (itemId) this.awaitingText.delete(itemId);
    if (this.awaitingText.size === 0) for (const w of this.textWaiters.splice(0)) w();
  }

  /** Until every committed caller turn has its transcript, or the wait runs out. */
  private transcriptsSettled(): Promise<void> {
    if (this.awaitingText.size === 0) return Promise.resolve();
    return new Promise((resolve) => {
      const cancel = this.d.clock.setTimer(TRANSCRIPT_WAIT_MS, () => {
        // Never wait for these again: a turn with no words (noise) has none coming.
        this.awaitingText.clear();
        this.textArrived(undefined);
      });
      this.textWaiters.push(() => {
        cancel();
        resolve();
      });
    });
  }

  /** Same listening predicate as the phone: nobody speaking, nothing playing,
   * nothing requested or running. */
  private updateIdle(): void {
    if (this.closed) return;
    const listening = !this.ending && !this.userSpeaking && !this.playing && this.coordinator.idle && this.pendingTools === 0;
    if (!listening) {
      this.idle?.();
      this.idle = null;
      return;
    }
    if (this.idle) return;
    this.idle = this.d.clock.setTimer(this.d.policy.silenceMs, () => {
      this.idle = null;
      this.strikes += 1;
      if (this.strikes >= MAX_SILENCE_STRIKES) return this.end('silence');
      this.coordinator.request('reprompt', { instructions: this.d.policy.reprompt, toolChoice: 'none' });
    });
  }

  private end(reason: 'goodbye' | 'silence' | 'model_unavailable'): void {
    if (this.closed) return;
    this.close();
    this.d.onEnd(reason);
  }

  close(): void {
    this.closed = true;
    this.idle?.();
    this.farewell?.();
    this.coordinator.close();
  }

  /** Events not yet sent with a tool call (for finalisation diagnostics). */
  get unsentEvents(): readonly TurnEvent[] {
    return this.events;
  }
}

/** The browser's clock, as the coordinator wants it. */
export const browserClock: CoordinatorClock = {
  now: () => new Date(),
  setTimer: (ms, cb) => {
    const h = setTimeout(cb, ms);
    return () => clearTimeout(h);
  },
};
